import { useEffect } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  TimePicker,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { Plus, Trash } from '@phosphor-icons/react';
import { tokens } from '../../theme';
import { ImageUploadField } from '../../components/ImageUploadField';
import type { Branch, Chain } from '../catalog/api';
import {
  TYPE_OPTIONS,
  WEEKDAY_OPTIONS,
  type Content,
  type ContentType,
  type UpsertContentPayload,
  type Weekday,
} from './api';

/** `TimeOnly` trên dây là `HH:mm:ss`; hiển thị tới phút vì model không lưu giây. */
const WIRE_TIME = 'HH:mm:ss';
const DISPLAY_TIME = 'HH:mm';
const WIRE_DATE = 'YYYY-MM-DD';
const DISPLAY_DATE = 'DD/MM/YYYY';

interface ScheduleRow {
  startsAt?: Dayjs;
  endsAt?: Dayjs;
  daysOfWeek?: Weekday[];
}

interface FormValues {
  title: string;
  type: ContentType;
  body?: string;
  imageUrl?: string;
  chainId?: string | null;
  branchIds: string[];
  startsOn: Dayjs;
  endsOn?: Dayjs | null;
  sortOrder: number;
  schedules: ScheduleRow[];
  excludedDates: Dayjs[];
}

interface Props {
  content: Content | null;
  isCreating: boolean;
  chains: Chain[];
  branches: Branch[];
  submitting: boolean;
  onSubmit: (payload: UpsertContentPayload) => void;
  onClose: () => void;
}

function buildPayload(values: FormValues): UpsertContentPayload {
  return {
    type: values.type,
    chainId: values.chainId ?? null,
    title: values.title.trim(),
    body: values.body?.trim() || null,
    imageUrl: values.imageUrl?.trim() || null,
    startsOn: values.startsOn.format(WIRE_DATE),
    endsOn: values.endsOn ? values.endsOn.format(WIRE_DATE) : null,
    sortOrder: values.sortOrder ?? 0,
    branchIds: values.branchIds ?? [],
    schedules: (values.schedules ?? [])
      .filter((row): row is ScheduleRow & { startsAt: Dayjs; endsAt: Dayjs } =>
        Boolean(row?.startsAt && row?.endsAt),
      )
      .map((row) => ({
        startsAt: row.startsAt.format(WIRE_TIME),
        endsAt: row.endsAt.format(WIRE_TIME),
        daysOfWeek: row.daysOfWeek ?? [],
      })),
    excludedDates: (values.excludedDates ?? []).map((d) => d.format(WIRE_DATE)),
  };
}

export function ContentFormModal({
  content,
  isCreating,
  chains,
  branches,
  submitting,
  onSubmit,
  onClose,
}: Props) {
  const [form] = Form.useForm<FormValues>();
  const open = content !== null || isCreating;

  const chainId = Form.useWatch('chainId', form) as string | null | undefined;

  const branchOptions = branches
    .filter((b) => !chainId || b.chainId === chainId)
    .map((b) => ({ value: b.id, label: b.name }));

  useEffect(() => {
    if (!open) return;

    if (content) {
      form.setFieldsValue({
        title: content.title,
        type: content.type,
        body: content.body ?? undefined,
        imageUrl: content.imageUrl ?? undefined,
        chainId: content.chainId,
        branchIds: content.branchIds,
        startsOn: dayjs(content.startsOn),
        endsOn: content.endsOn ? dayjs(content.endsOn) : undefined,
        sortOrder: content.sortOrder,
        schedules: content.schedules.map((s) => ({
          startsAt: dayjs(s.startsAt, WIRE_TIME),
          endsAt: dayjs(s.endsAt, WIRE_TIME),
          // Server trả đủ bảy thứ khi bài chạy mọi ngày. Giữ nguyên thì ô hiện bảy thẻ và
          // người sửa tưởng mình đã chọn tay từng thứ — rút về rỗng, nghĩa vẫn y hệt.
          daysOfWeek: s.daysOfWeek.length === 7 ? [] : s.daysOfWeek,
        })),
        excludedDates: content.excludedDates.map((d) => dayjs(d)),
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        type: 'Offer',
        startsOn: dayjs(),
        sortOrder: 0,
        branchIds: [],
        schedules: [],
        excludedDates: [],
      });
    }
  }, [content, open, form]);

  const scheduleRow = (name: number, remove: (index: number) => void) => {
    return (
      <div key={name} style={{ marginBottom: 10 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '120px 120px 1fr 40px',
            gap: 8,
            alignItems: 'start',
          }}
        >
          <Form.Item
            name={[name, 'startsAt']}
            style={{ marginBottom: 0 }}
            rules={[{ required: true, message: 'Chưa nhập giờ bắt đầu.' }]}
          >
            <TimePicker format={DISPLAY_TIME} minuteStep={5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name={[name, 'endsAt']}
            style={{ marginBottom: 0 }}
            dependencies={[['schedules', name, 'startsAt']]}
            rules={[
              { required: true, message: 'Chưa nhập giờ kết thúc.' },
              {
                validator: (_, value: Dayjs | undefined) => {
                  const from = form.getFieldValue(['schedules', name, 'startsAt']) as
                    | Dayjs
                    | undefined;
                  if (!value || !from) return Promise.resolve();
                  if (!value.isAfter(from)) {
                    return Promise.reject(
                      new Error('Giờ kết thúc phải sau giờ bắt đầu trong cùng ngày.'),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TimePicker format={DISPLAY_TIME} minuteStep={5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name={[name, 'daysOfWeek']} style={{ marginBottom: 0 }}>
            <Select
              mode="multiple"
              allowClear
              options={WEEKDAY_OPTIONS}
              placeholder="Để trống = mọi ngày"
            />
          </Form.Item>
          <Button
            type="text"
            danger
            icon={<Trash size={16} />}
            aria-label={`Xóa khung giờ ${name + 1}`}
            onClick={() => remove(name)}
          />
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={open}
      title={isCreating ? 'Đăng nội dung mới' : `Sửa “${content?.title ?? ''}”`}
      okText="Lưu"
      cancelText="Hủy"
      width={800}
      confirmLoading={submitting}
      destroyOnHidden
      onCancel={onClose}
      onOk={() => {
        form
          .validateFields()
          .then((values) => onSubmit(buildPayload(values)))
          .catch(() => {});
      }}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="title"
          label="Tiêu đề"
          rules={[{ required: true, message: 'Chưa nhập tiêu đề.' }]}
        >
          <Input maxLength={200} />
        </Form.Item>

        <Form.Item name="type" label="Loại" rules={[{ required: true }]}>
          <Select options={TYPE_OPTIONS} />
        </Form.Item>

        <Form.Item name="body" label="Mô tả">
          <Input.TextArea rows={3} maxLength={8000} />
        </Form.Item>

        <Form.Item name="imageUrl" label="Ảnh">
          <ImageUploadField />
        </Form.Item>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
          }}
        >
          <Form.Item
            name="startsOn"
            label="Hiện từ ngày"
            rules={[{ required: true, message: 'Chưa chọn ngày bắt đầu.' }]}
          >
            <DatePicker format={DISPLAY_DATE} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="endsOn"
            label="Tới ngày"
            extra="Để trống là hiện tới khi tắt tay."
            dependencies={['startsOn']}
            rules={[
              {
                validator: (_, value: Dayjs | undefined) => {
                  const from = form.getFieldValue('startsOn') as Dayjs | undefined;
                  if (!value || !from) return Promise.resolve();
                  if (value.isBefore(from, 'day')) {
                    return Promise.reject(
                      new Error('Ngày kết thúc phải từ ngày bắt đầu trở đi.'),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <DatePicker format={DISPLAY_DATE} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="chainId" label="Chuỗi">
            <Select
              allowClear
              placeholder="Mọi chuỗi"
              options={chains.map((c) => ({ value: c.id, label: c.name }))}
              onChange={(value: string | undefined) => {
                // Đổi chuỗi thì bỏ những chi nhánh không còn thuộc chuỗi đó. Không bỏ thì
                // chúng biến mất khỏi danh sách lựa chọn mà vẫn còn trong payload — phạm vi
                // hiển thị khác hẳn cái người vừa nhìn thấy.
                if (!value) return;
                const selected = (form.getFieldValue('branchIds') as string[]) ?? [];
                const kept = selected.filter(
                  (id) => branches.find((b) => b.id === id)?.chainId === value,
                );
                if (kept.length !== selected.length) form.setFieldValue('branchIds', kept);
              }}
            />
          </Form.Item>

          <Form.Item
            name="branchIds"
            label="Chi nhánh"
            extra="Để trống là toàn chuỗi. Đây là cấu hình của từng bài, không phụ thuộc vai trò người đăng."
          >
            <Select
              mode="multiple"
              allowClear
              options={branchOptions}
              placeholder="Toàn chuỗi"
            />
          </Form.Item>
        </div>

        <Form.Item
          name="sortOrder"
          label="Thứ tự hiện"
          extra="Nhỏ hơn hiện trước trong dải cuộn ngang của khách."
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>

        <Typography.Title level={5} style={{ marginTop: 4 }}>
          Khung giờ trong ngày
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 12 }}>
          Thêm <b>nhiều khung giờ</b> cho cùng một bài: ưu đãi trưa và ưu đãi tối của cùng một combo
          là hai khung, không phải hai bài. Không khai khung nào nghĩa là <b>hiện cả ngày</b> — đúng
          mặc định cho tin tức.
        </Typography.Paragraph>

        <Form.List name="schedules">
          {(fields, { add, remove }) => (
            <>
              {fields.length > 0 && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '120px 120px 1fr 40px',
                    gap: 8,
                    marginBottom: 6,
                    fontSize: 12,
                    color: tokens.color.ink3,
                  }}
                >
                  <span>Từ giờ</span>
                  <span>Tới giờ</span>
                  <span>Thứ trong tuần</span>
                  <span />
                </div>
              )}
              {fields.map((field) => scheduleRow(field.name, remove))}
              <Button
                size="small"
                icon={<Plus size={14} />}
                style={{ marginBottom: 16 }}
                onClick={() => add({ daysOfWeek: [] })}
              >
                Thêm khung giờ
              </Button>
            </>
          )}
        </Form.List>

        <Form.Item
          name="excludedDates"
          label="Ngày loại trừ"
          extra="Tết và lễ khai vào đây. Lịch âm không tính được bằng công thức nên phải khai tay."
        >
          <DatePicker multiple format={DISPLAY_DATE} style={{ width: '100%' }} />
        </Form.Item>

        <Alert
          type="warning"
          showIcon
          message="Giờ tính theo giờ Việt Nam"
          description="Muốn chạy xuyên đêm thì tách hai khung trong ngày: 22:00–23:59 và 00:00–02:00."
        />
      </Form>
    </Modal>
  );
}
